import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styles from "./Bitiruvchilar.module.css";
import activityStyles from "./activityCards.module.css";
import classes from "./Activities.module.css";
import { testUrl } from "../BaseData";
import Pagination from "@mui/material/Pagination";

const ALUMNI_PER_PAGE = 3;
// How many cards to keep mounted in the DOM (visible window + a small buffer
// below the fold so the next card is ready before it scrolls into view).
const RENDER_WINDOW = 12;
// One-card upward glide (ms) and the pause before the next card starts.
const GLIDE_MS = 2300;
const PAUSE_MS = 1300;

// Small line icons (inherit color via currentColor). Decorative -> aria-hidden.
const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false,
};
const IconEducation = () => (
    <svg {...iconProps}>
        <path d="M22 10 12 5 2 10l10 5 10-5z" />
        <path d="M6 12v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
    </svg>
);
const IconSearch = () => (
    <svg {...iconProps}>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
    </svg>
);
const IconUser = () => (
    <svg {...iconProps}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const Bitiruvchilar = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // Activities state
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [activities, setActivities] = useState([]);
    const [pageApi, setPageApi] = useState(1);
    const activityItemsPerPage = 9;

    // Alumni state
    const [alumniLoading, setAlumniLoading] = useState(true);
    const [alumni, setAlumni] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [alumniPage, setAlumniPage] = useState(1);

    // Circular auto-scroll: `order` is the current rotation of alumni indices.
    // Each step glides the list up by one card, then the top card is moved to
    // the end of `order` (wrapping around) and the transform snaps back to 0.
    const [order, setOrder] = useState([]);
    const [translateY, setTranslateY] = useState(0);
    const [animate, setAnimate] = useState(false);
    const [paused, setPaused] = useState(false);
    // Viewport is sized to exactly ALUMNI_PER_PAGE cards, so the list overflows
    // and scrolls (and only 3 show at a time).
    const [viewportHeight, setViewportHeight] = useState(null);
    const viewportRef = useRef(null);
    const listRef = useRef(null);

    // Fetch activities
    useEffect(() => {
        setActivitiesLoading(true);
        axios.get(testUrl + "/api/activitiestype/")
            .then((res) => {
                const types = res.data;
                const targetType = types.find(type =>
                    (type.name_uz && type.name_uz.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_ru && type.name_ru.toLowerCase().includes("bitiruvchi")) ||
                    (type.name_en && type.name_en.toLowerCase().includes("bitiruvchi")) ||
                    type.name_uz === "Bizning bitiruvchilar"
                );
                const shortName = targetType && targetType.short_name ? targetType.short_name : "bizning-bitiruvchilar";
                return axios.get(testUrl + `/api/activities/${shortName}/`);
            })
            .then((response) => {
                if (response && response.data) {
                    setActivities(response.data);
                }
            })
            .catch((err) => console.error("Error fetching activities:", err))
            .finally(() => setActivitiesLoading(false));
    }, [i18n.language]);

    // Fetch alumni
    useEffect(() => {
        setAlumniLoading(true);
        axios.get(testUrl + "/api/alumni/")
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setAlumni(data);
            })
            .catch((err) => console.error("Error fetching alumni:", err))
            .finally(() => setAlumniLoading(false));
    }, [i18n.language]);

    // Filter alumni
    const filteredAlumni = useMemo(() => {
        let result = alumni;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(a =>
                (a.full_name && a.full_name.toLowerCase().includes(q)) ||
                (a.name_uz && a.name_uz.toLowerCase().includes(q)) ||
                (a.surname_uz && a.surname_uz.toLowerCase().includes(q)) ||
                (a.faculty && a.faculty.toLowerCase().includes(q)) ||
                (a.education_field && a.education_field.toLowerCase().includes(q)) ||
                (a.workplace && a.workplace.toLowerCase().includes(q)) ||
                (a.address && a.address.toLowerCase().includes(q))
            );
        }
        return result;
    }, [alumni, searchQuery]);

    const totalAlumniPages = Math.max(1, Math.ceil(filteredAlumni.length / ALUMNI_PER_PAGE));

    // Only the first RENDER_WINDOW cards of the current rotation are mounted.
    // Guard against indices left over from a previous (longer) list mid-update.
    const activeOrder = order.length ? order : filteredAlumni.map((_, i) => i);
    const windowItems = activeOrder
        .filter((i) => i < filteredAlumni.length)
        .slice(0, RENDER_WINDOW);

    // Reset the rotation whenever the (filtered) list changes.
    useEffect(() => {
        setOrder(filteredAlumni.map((_, i) => i));
        setTranslateY(0);
        setAnimate(false);
        setAlumniPage(1);
    }, [filteredAlumni]);

    // Size the viewport to exactly ALUMNI_PER_PAGE cards (measured from the
    // rendered cards, so it adapts to card content). Re-measure on resize.
    useLayoutEffect(() => {
        const measure = () => {
            const list = listRef.current;
            if (!list || list.children.length === 0) return;
            const gap = 16;
            const n = Math.min(ALUMNI_PER_PAGE, list.children.length);
            let h = 0;
            for (let i = 0; i < n; i++) h += list.children[i].offsetHeight;
            h += gap * (n - 1) + 8; // + list vertical padding
            setViewportHeight(h);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [filteredAlumni, alumniLoading]);

    // Manual pagination: re-seed the rotation so the chosen page's first card
    // leads. The page indicator stays put after that (it does not auto-advance).
    const goToAlumniPage = (page) => {
        const n = filteredAlumni.length;
        if (n === 0) return;
        const start = ((page - 1) * ALUMNI_PER_PAGE) % n;
        setOrder(Array.from({ length: n }, (_, i) => (start + i) % n));
        setTranslateY(0);
        setAnimate(false);
        setAlumniPage(page);
    };

    // Circular auto-scroll: glide up one card, wrap the top card to the end,
    // snap back, pause, repeat — an endless bottom-to-top loop.
    useEffect(() => {
        if (paused || alumniLoading || filteredAlumni.length <= ALUMNI_PER_PAGE) return;

        let cancelled = false;
        let tGlide, tPause;

        const step = () => {
            if (cancelled) return;
            const list = listRef.current;
            const viewport = viewportRef.current;
            // Nothing mounted yet, or content fits without scrolling -> wait.
            if (!list || !viewport || list.children.length === 0 ||
                list.scrollHeight <= viewport.clientHeight + 4) {
                tPause = setTimeout(step, PAUSE_MS);
                return;
            }

            const first = list.children[0];
            const dist = first.offsetHeight + 16; // + flex gap

            // Glide the whole list up by exactly one card.
            setAnimate(true);
            setTranslateY(-dist);

            tGlide = setTimeout(() => {
                if (cancelled) return;
                // Move the top card to the end and snap back — seamless.
                setAnimate(false);
                setTranslateY(0);
                setOrder((prev) => (prev.length ? [...prev.slice(1), prev[0]] : prev));
                tPause = setTimeout(step, PAUSE_MS);
            }, GLIDE_MS);
        };

        tPause = setTimeout(step, PAUSE_MS);
        return () => { cancelled = true; clearTimeout(tGlide); clearTimeout(tPause); };
    }, [paused, alumniLoading, filteredAlumni]);

    // Activities pagination
    const currentActivities = activities.slice(
        (pageApi - 1) * activityItemsPerPage,
        pageApi * activityItemsPerPage
    );

    const getDisplayName = (a) => {
        if (a.full_name) return a.full_name;
        return `${a.surname_uz || ''} ${a.name_uz || ''}`.trim() || "Noma'lum";
    };

    // First 1-2 letters of the name for the no-photo monogram (Latin/Cyrillic)
    const getInitials = (a) => {
        const name = (a.full_name || `${a.surname_uz || ''} ${a.name_uz || ''}`).trim();
        const letters = name.split(/\s+/)
            .map((p) => p[0])
            .filter((ch) => ch && /\p{L}/u.test(ch));
        return letters.slice(0, 2).join('').toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        // birth_date is a "YYYY-MM-DD" string; read the year directly to avoid
        // new Date() UTC/local timezone shifts on Jan-1 dates.
        const match = String(dateStr).match(/(\d{4})/);
        return match ? match[1] : dateStr;
    };

    // Ta'lim yo'nalishi. Excel'da "Ta'lim yo'nalishi" ustuni ko'pincha bo'sh
    // qoladi, yo'nalish nomi ("Filologiya va tarix") esa "Fakultet" ustunida
    // keladi — shu sabab education_field bo'sh bo'lsa faculty'ga tushamiz.
    // Eski importlarda faculty'ga JSHIR (raqamlar) tushib qolgan — uni chiqarmaymiz.
    const getEducationField = (a) => {
        const edu = a.education_field && String(a.education_field).trim();
        if (edu) return edu;
        const fac = a.faculty && String(a.faculty).trim();
        if (fac && !/^\d{9,}$/.test(fac)) return fac;
        return '';
    };

    // Renders a single graduate card. keyPrefix keeps React keys unique between
    // the original set and the duplicated set used for the seamless scroll loop.
    const renderAlumniCard = (item, keyPrefix) => {
        const educationField = getEducationField(item);
        // Har bir yozuvga "lavozimida ishlaydi" qo'shamiz. Faqat matn allaqachon
        // "lavozim" so'zini o'z ichiga olsa qo'shmaymiz — takror bo'lmasin.
        const rawWorkplace = String(item.workplace || '').trim();
        const rawPosition = String(item.position || '').trim();
        const employmentText = `${rawWorkplace} ${rawPosition}`.toLowerCase();
        const appendLavozim = !!employmentText && !/lavozim/.test(employmentText);
        // Suffix qo'shsak, oxirgi segmentdagi nuqtani olib tashlaymiz
        // ("...tarbiyachi." -> "...tarbiyachi lavozimida ishlaydi.")
        let wpText = rawWorkplace;
        let posText = rawPosition;
        if (appendLavozim) {
            if (posText) posText = posText.replace(/[.!?…\s]+$/, '');
            else wpText = wpText.replace(/[.!?…\s]+$/, '');
        }
        // Suffix qo'shmasak, matn nuqta bilan tugamagan bo'lsa nuqta qo'yamiz
        const employmentEndsWithPunct = /[.!?…:]$/.test(rawPosition || rawWorkplace);
        return (
            <div key={`${keyPrefix}-${item.id}`} className={styles.alumniCard}>
                <div className={styles.cardBody}>
                    {/* Photo + achievements below it */}
                    <div className={styles.cardPhotoSection}>
                        <div className={styles.cardPhotoFrame}>
                            {item.image ? (
                                <img src={item.image} alt={getDisplayName(item)} className={styles.cardPhoto} />
                            ) : (
                                <div className={styles.cardPhotoPlaceholder} aria-hidden="true">
                                    {getInitials(item) || <IconUser />}
                                </div>
                            )}
                        </div>

                        <p className={styles.infoName}>{getDisplayName(item)}</p>
                    </div>

                    {/* Info */}
                    <div className={styles.cardInfoSection}>
                        {(item.address || item.birth_date) && (
                            <div className={styles.infoRow}>
                                <p className={styles.infoText}>
                                    {item.address && <>{item.address}{item.birth_date ? 'da' : '.'}</>}
                                    {item.address && item.birth_date && <br />}
                                    {item.birth_date && <>{formatDate(item.birth_date)} yilda tug'ilgan.</>}
                                </p>
                            </div>
                        )}

                        {(item.graduation_year || educationField) && (
                            <div className={styles.infoRow}>
                                <p className={styles.infoText}>
                                    {item.graduation_year && `${item.graduation_year} yilda `}
                                    Renessans universitetining{' '}
                                    {educationField ? (
                                        <>
                                            <span className={styles.infoHighlight}>{educationField}</span>
                                            {' '}ta'lim yo'nalishini tamomlagan.
                                        </>
                                    ) : (
                                        <>ta'lim yo'nalishini tamomlagan.</>
                                    )}
                                </p>
                            </div>
                        )}

                        {(item.workplace || item.position) && (
                            <div className={styles.infoRow}>
                                <p className={styles.infoText}>
                                    Hozirgi vaqtda
                                    {wpText && <> {wpText}{posText ? 'da' : ''}</>}
                                    {posText && <>{' '}{posText}</>}
                                    {appendLavozim ? <>{' '}lavozimida ishlaydi.</> : (employmentEndsWithPunct ? '' : '.')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {item.achievements && String(item.achievements).trim() && (
                    <div className={styles.cardAchievements}>
                        <div className={styles.achievementsHeader}>Yutuqlari</div>
                        <p className={styles.achievementsText}>
                            {String(item.achievements).trim()}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.pageWrapper}>
            {/* ===== BITIRUVCHILARIMIZ SECTION ===== */}
            <section className={styles.alumniSection}>
                {/* Section header */}
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderLeft}>
                        <h1 className={styles.sectionTitle}>BITIRUVCHILARIMIZ</h1>
                    </div>
                    <div className={styles.sectionHeaderRight}>
                        <div className={styles.searchWrapper}>
                            <span className={styles.searchIcon}><IconSearch /></span>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={t("Qidirish...")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Alumni cards grid */}
                {alumniLoading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.spinner}></div>
                        {t("Yuklanmoqda...")}
                    </div>
                ) : filteredAlumni.length > 0 ? (
                    <>
                        <div
                            className={styles.alumniViewport}
                            ref={viewportRef}
                            style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}
                            onMouseEnter={() => setPaused(true)}
                            onMouseLeave={() => setPaused(false)}
                        >
                            <div
                                className={styles.alumniList}
                                ref={listRef}
                                style={{
                                    transform: `translateY(${translateY}px)`,
                                    transition: animate
                                        ? `transform ${GLIDE_MS}ms cubic-bezier(0.45, 0, 0.55, 1)`
                                        : 'none',
                                }}
                            >
                                {windowItems.map((idx) => renderAlumniCard(filteredAlumni[idx], 'a'))}
                            </div>
                        </div>

                        {totalAlumniPages > 1 && (
                            <div className={styles.alumniPagination}>
                                <Pagination
                                    onChange={(e, value) => goToAlumniPage(value)}
                                    count={totalAlumniPages}
                                    page={alumniPage}
                                    variant="outlined"
                                    shape="rounded"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}><IconEducation /></div>
                        <p className={styles.emptyStateText}>Bitiruvchilar topilmadi</p>
                        <p className={styles.emptyStateSubText}>
                            Ma'lumotlar admin paneldan Excel orqali yuklanadi
                        </p>
                    </div>
                )}

            </section>

            {/* ===== FAOLIYAT SECTION ===== */}
            <section className={styles.activitiesSection}>
                <div data-aos="fade-up" className={classes["container"]}>
                    <h1 className={classes["page-title"]}>{t("Faoliyat")}</h1>
                    {activitiesLoading ? (
                        <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            {t("Yuklanmoqda...")}
                        </div>
                    ) : (
                        <>
                            <div className={activityStyles.facultyGrid}>
                                {currentActivities.filter(Boolean).map((item) => (
                                    <div key={item.id} className={activityStyles.activityCard}
                                        onClick={() => navigate(`/activity/${item.id}`)}>
                                        <img src={item.image} alt=" " className={activityStyles.activityImage} />
                                        <div className={activityStyles.activityInfo}>
                                            <h3>{item[`name_${i18n.language}`] || item.name_uz}</h3>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {activities.length > activityItemsPerPage && (
                                <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                                    <Pagination
                                        onChange={(e, value) => setPageApi(value)}
                                        count={Math.ceil(activities.length / activityItemsPerPage)}
                                        page={pageApi}
                                        variant="outlined"
                                        shape="rounded"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Bitiruvchilar;
